import { Link, useParams } from 'react-router-dom';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useConcelho } from '../hooks/useConcelhos';
import { RISK_COLORS } from '../types';
import {
  RISK_PT,
  fmtArea,
  fmtDate,
  fmtDateTime,
  fmtInt,
  monthName,
} from '../lib/concelhos';
import { useConcelhosLive } from '../hooks/useConcelhos';
import RiskDistributionBar from '../components/RiskDistributionBar';
import JsonLd from '../components/JsonLd';
import { bandKindOf, FLAGGED_SHARE_PCT, SHARE_CAVEAT } from '../lib/riskText';

const SITE = 'https://tutela.land';

export default function ConcelhoRisco() {
  const { slug = '' } = useParams();
  const { concelho, live, distrito, bySlug, isLoading } = useConcelho(slug);
  const liveDoc = useConcelhosLive().data;

  const nome = concelho?.nome ?? '';
  const path = `/ignis/risco/${slug}`;
  useDocumentMeta(
    concelho
      ? `Risco de incêndio em ${nome}: previsão a 1 km | Tutela IGNIS`
      : 'Risco de incêndio | Tutela IGNIS',
    concelho
      ? `Previsão do risco de incêndio rural no concelho de ${nome} (${concelho.distrito}), quadrícula a quadrícula (1 km). Padrão histórico de ignições e leitura honesta face ao IPMA.`
      : undefined,
    path,
  );

  if (isLoading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-ink-950 px-6 py-20 text-center text-mist-muted">
        A carregar…
      </div>
    );
  }

  if (!concelho) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-ink-950 px-6 py-20 text-center text-mist">
        <h1 className="font-display text-2xl font-bold">Concelho não encontrado</h1>
        <p className="mt-4 text-mist-muted">
          Não temos uma página para “{slug}”.{' '}
          <Link to="/ignis/risco" className="text-ember hover:underline">
            Ver todos os concelhos
          </Link>
          .
        </p>
      </div>
    );
  }

  const fh = concelho.fire_history;
  const forDate = liveDoc?.for_date ? fmtDate(liveDoc.for_date) : 'hoje';
  // W5: wording keys on the served model family (share bands for the rebuilt model, D9).
  const share = bandKindOf(liveDoc?.model_version) === 'share';

  // FAQ answers built from real data (honest, templated).
  const faq: [string, string][] = [
    [
      `Qual é o risco de incêndio em ${nome} hoje?`,
      live
        ? share
          ? `Para ${forDate}, ${live.mod_plus_pct}% das quadrículas de 1 km de ${nome} estão entre os ${FLAGGED_SHARE_PCT}% do território continental com maior risco de ignição segundo o IGNIS; a classe dominante é ${RISK_PT[live.dominant]}. ${SHARE_CAVEAT} É uma previsão automática, emitida uma vez por dia, complementar ao aviso oficial do IPMA.`
          : `Para ${forDate}, o modelo IGNIS classifica ${live.mod_plus_pct}% das quadrículas de 1 km de ${nome} em risco Moderado ou superior; a classe dominante é ${RISK_PT[live.dominant]}. É uma previsão automática, complementar ao aviso oficial do IPMA.`
        : share
          ? `A previsão do IGNIS para ${nome} é feita quadrícula a quadrícula (1 km) e emitida uma vez por dia, de manhã.`
          : `A previsão do IGNIS para ${nome} é feita quadrícula a quadrícula (1 km) e atualizada ao longo do dia.`,
    ],
    [
      `Como é calculado o risco de incêndio em ${nome}?`,
      share
        ? `O IGNIS parte do histórico de ignições de cada quadrícula de 1 km em cada mês e aprende, com a meteorologia do dia (IFS, ERA5-Land, índices FWI) e as características do terreno, o desvio face a esse histórico. Ordena o território: Extremo, Alto e Moderado são o 1, 2 e 5 por cento das quadrículas com maior risco nesse dia. Cobre todo o concelho de ${nome}, uma resolução mais fina do que o nível único por concelho publicado pelo IPMA.`
        : `O IGNIS estima o risco com um ensemble de machine learning sobre 63 variáveis (meteorologia, índices FWI, terreno, ocupação do solo, histórico de fogos). Cobre todo o concelho de ${nome} em quadrículas de 1 km, uma resolução mais fina do que o nível único por concelho publicado pelo IPMA.`,
    ],
  ];
  if (fh) {
    faq.push([
      `${nome} tem muitos incêndios?`,
      `Desde ${fh.since_year}, o ICNF registou ${fmtInt(fh.n_fires)} ignições no concelho de ${nome}, com ${fmtArea(fh.area_ha)} de área ardida. O pior ano foi ${fh.worst_year} e a época de maior atividade é ${monthName(fh.peak_month)}.`,
    ]);
  }

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'IGNIS', item: `${SITE}/ignis` },
      { '@type': 'ListItem', position: 2, name: 'Risco por concelho', item: `${SITE}/ignis/risco` },
      {
        '@type': 'ListItem',
        position: 3,
        name: concelho.distrito,
        item: `${SITE}/ignis/risco/distrito/${concelho.distrito_slug}`,
      },
      { '@type': 'ListItem', position: 4, name: nome, item: `${SITE}${path}` },
    ],
  };
  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map(([q, a]) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  const neighbours = concelho.neighbours
    .map((s) => bySlug.get(s))
    .filter((c): c is NonNullable<typeof c> => Boolean(c));

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-ink-950 text-mist">
      <JsonLd id="breadcrumb" data={breadcrumb} />
      <JsonLd id="faq" data={faqLd} />
      <div className="mx-auto max-w-3xl px-6 py-14">
        {/* Breadcrumb */}
        <nav className="font-mono text-xs text-mist-faint">
          <Link to="/ignis" className="hover:text-mist">IGNIS</Link>
          {' / '}
          <Link to="/ignis/risco" className="hover:text-mist">Risco</Link>
          {' / '}
          <Link to={`/ignis/risco/distrito/${concelho.distrito_slug}`} className="hover:text-mist">
            {concelho.distrito}
          </Link>
          {' / '}
          <span className="text-mist-muted">{nome}</span>
        </nav>

        <h1 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
          Risco de incêndio em {nome}
        </h1>
        <p className="mt-4 text-lg leading-relaxed text-mist-muted">
          Previsão do risco de incêndio rural para o concelho de {nome} ({concelho.distrito}),
          quadrícula a quadrícula de 1 km. Distrito de {concelho.distrito}, {concelho.nuts3}
          {' · '}
          {fmtInt(concelho.area_km2)} km² · {fmtInt(concelho.n_cells)} quadrículas.
        </p>

        {/* ---- Forecast-first hero ---- */}
        <section className="mt-8 rounded-2xl border border-ink-700 bg-ink-900 p-6">
          <div className="flex items-baseline justify-between">
            <h2 className="font-display text-lg font-bold">Previsão para {forDate}</h2>
            {liveDoc?.prediction_time && (
              <span className="font-mono text-[11px] text-mist-faint">
                {share ? 'emitido' : 'atualizado'} {fmtDateTime(liveDoc.prediction_time)}
              </span>
            )}
          </div>

          {live ? (
            <>
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <span
                  className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5 font-display text-sm font-bold"
                  style={{ backgroundColor: `${RISK_COLORS[live.dominant]}22`, color: RISK_COLORS[live.dominant] }}
                >
                  <span
                    className="inline-block h-3 w-3 rounded-full"
                    style={{ backgroundColor: RISK_COLORS[live.dominant] }}
                  />
                  Classe dominante: {RISK_PT[live.dominant]}
                </span>
                <span className="text-mist-muted">
                  <strong className="text-mist">{live.mod_plus_pct}%</strong>{' '}
                  {share
                    ? `do concelho entre os ${FLAGGED_SHARE_PCT}% do território com maior risco`
                    : 'do concelho em risco Moderado ou superior'}
                </span>
              </div>
              <div className="mt-5">
                <RiskDistributionBar risk={live.risk} total={live.n_cells} />
              </div>
            </>
          ) : (
            <p className="mt-4 text-mist-muted">
              Previsão indisponível de momento para este concelho.
            </p>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/ignis/mapa-nacional"
              className="inline-flex items-center gap-2 rounded-lg bg-ember px-5 py-2.5 text-sm font-semibold text-ink-950 transition-all hover:brightness-110"
            >
              Ver no mapa nacional
            </Link>
            <Link
              to="/ignis/validacao"
              className="inline-flex items-center gap-2 rounded-lg border border-ink-700 px-5 py-2.5 text-sm font-medium text-mist transition-colors hover:bg-ink-800"
            >
              Como validamos
            </Link>
          </div>
        </section>

        {/* ---- Padrão histórico (compact, model-evidence framing) ---- */}
        {fh && (
          <section className="mt-8">
            <h2 className="font-display text-lg font-bold">Padrão histórico</h2>
            <p className="mt-3 leading-relaxed text-mist-muted">
              Desde {fh.since_year}, o ICNF registou{' '}
              <strong className="text-mist">{fmtInt(fh.n_fires)} ignições</strong> no concelho de{' '}
              {nome}, com <strong className="text-mist">{fmtArea(fh.area_ha)}</strong> de área
              ardida. O pior ano foi <strong className="text-mist">{fh.worst_year}</strong> e a época
              de maior atividade é <strong className="text-mist">{monthName(fh.peak_month)}</strong>.
              Este padrão histórico é um dos sinais que o modelo integra para prever o risco, não é
              a previsão em si.
            </p>
          </section>
        )}

        {/* ---- Honest framing ---- */}
        <section className="mt-8 rounded-2xl border border-ink-700 bg-ink-900/60 p-6">
          <h2 className="font-display text-lg font-bold">Como ler este risco</h2>
          <ul className="mt-3 space-y-2 leading-relaxed text-mist-muted">
            {share ? (
              <li>
                A percentagem indica a fração do concelho em cada classe. As classes são quotas fixas
                do território continental nesse dia (Extremo 1%, Alto 2%, Moderado 5%), atribuídas
                por ordem de risco. <em>{SHARE_CAVEAT}</em>
              </li>
            ) : (
              <li>
                A percentagem indica a fração do concelho em cada classe de risco. É uma{' '}
                <em>pontuação relativa</em> do modelo, não uma probabilidade de ignição.
              </li>
            )}
            <li>
              O IGNIS resolve o risco em quadrículas de 1 km, dentro do concelho; o IPMA publica um
              único nível por concelho. Somos um <strong className="text-mist">complemento</strong> ao
              IPMA e ao EFFIS, não um substituto do aviso oficial.
            </li>
            <li>
              Em caso de perigo, siga sempre as indicações da{' '}
              <span className="text-mist">Proteção Civil (112 / 117)</span> e do IPMA.
            </li>
          </ul>
        </section>

        {/* ---- Internal links ---- */}
        {neighbours.length > 0 && (
          <section className="mt-8">
            <h2 className="font-display text-lg font-bold">Concelhos vizinhos</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {neighbours.map((n) => (
                <Link
                  key={n.slug}
                  to={`/ignis/risco/${n.slug}`}
                  className="rounded-lg border border-ink-700 px-3 py-1.5 text-sm text-mist-muted transition-colors hover:bg-ink-800 hover:text-mist"
                >
                  {n.nome}
                </Link>
              ))}
            </div>
          </section>
        )}

        {distrito && (
          <p className="mt-8 text-sm text-mist-muted">
            Ver todos os concelhos do distrito de{' '}
            <Link to={`/ignis/risco/distrito/${distrito.slug}`} className="text-ember hover:underline">
              {distrito.nome}
            </Link>{' '}
            ({distrito.n_concelhos}).
          </p>
        )}

        <p className="mt-10 border-t border-ink-800 pt-5 font-mono text-[11px] leading-relaxed text-mist-faint">
          Limites administrativos: Direção-Geral do Território, CAOP 2025 (CC BY 4.0). Histórico de
          fogos: ICNF. Previsão: modelo IGNIS {liveDoc?.model_version}.
        </p>
      </div>
    </div>
  );
}
